import { forwardRef, useCallback } from 'react'
import { AlignHorizontalSpaceAround, AlignStartVertical, Loader2, Search, X } from 'lucide-react'
import { cn } from '../utils/cn'
import type { SearchOperator } from '../services/api.types'

interface SearchInputProps {
  value: string
  onChange: (value: string) => void
  placeholder?: string
  className?: string
  listboxId?: string
  activeDescendantId?: string
  /** Current match mode for `value` — always exactly one of the two is active. */
  searchOperator: SearchOperator
  onSearchOperatorChange: (operator: SearchOperator) => void
  /** Label shown as the "starts with" toggle's tooltip/aria-label. */
  startsWithLabel: string
  /** Label shown as the "contains" toggle's tooltip/aria-label. */
  containsLabel: string
  /**
   * State of the Enter-to-resolve-exact-code lookup (triggered from outside, via Enter
   * with nothing highlighted). `'searching'` shows a spinner in place of the operator
   * toggle. Coming back empty is silent by design — no visible change here at all.
   */
  exactMatchStatus?: 'idle' | 'searching'
}

export const SearchInput = forwardRef<HTMLInputElement, SearchInputProps>(function SearchInput(
  {
    value,
    onChange,
    placeholder = 'Search...',
    className,
    listboxId,
    activeDescendantId,
    searchOperator,
    onSearchOperatorChange,
    startsWithLabel,
    containsLabel,
    exactMatchStatus = 'idle',
  },
  ref
) {
  const handleClear = useCallback(() => {
    onChange('')
  }, [onChange])

  /**
   * El par de operadores, ahora con pictograma en vez del glifo de texto «Ab…»/«…Ab…»:
   * `AlignStartVertical` (alinea al inicio) para «empieza por» y
   * `AlignHorizontalSpaceAround` (distribuye alrededor) para «contiene». Siguen EN FLUJO,
   * no superpuestos — ver el comentario junto a `{operadores}` más abajo para el porqué
   * del posicionamiento. El nombre accesible sigue viniendo de `startsWithLabel` /
   * `containsLabel` vía `aria-label` (el ícono es puramente decorativo, `aria-hidden`),
   * exactamente como en `title` — nada de eso cambió, solo qué se dibuja.
   */
  const operadores =
    exactMatchStatus === 'searching' ? (
      <Loader2 className="lookup-field__search-resolving h-3.5 w-3.5 animate-spin" />
    ) : (
      <div
        className="lookup-field__search-operator-group"
        role="radiogroup"
        aria-label={`${startsWithLabel} / ${containsLabel}`}
      >
        <button
          type="button"
          className={cn(
            'lookup-field__search-operator-btn',
            searchOperator === 'startsWith' && 'lookup-field__search-operator-btn--active'
          )}
          onClick={() => onSearchOperatorChange('startsWith')}
          role="radio"
          aria-checked={searchOperator === 'startsWith'}
          aria-label={startsWithLabel}
          title={startsWithLabel}
          tabIndex={-1}
        >
          <AlignStartVertical
            className="lookup-field__search-operator-glyph h-3.5 w-3.5"
            aria-hidden="true"
          />
        </button>
        <button
          type="button"
          className={cn(
            'lookup-field__search-operator-btn',
            searchOperator === 'contains' && 'lookup-field__search-operator-btn--active'
          )}
          onClick={() => onSearchOperatorChange('contains')}
          role="radio"
          aria-checked={searchOperator === 'contains'}
          aria-label={containsLabel}
          title={containsLabel}
          tabIndex={-1}
        >
          <AlignHorizontalSpaceAround
            className="lookup-field__search-operator-glyph h-3.5 w-3.5"
            aria-hidden="true"
          />
        </button>
      </div>
    )

  return (
    <div className={cn('lookup-field__search-container', className)}>
      <Search className="lookup-field__search-icon" />
      <input
        ref={ref}
        type="text"
        className="lookup-field__search"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        aria-label={placeholder}
        aria-autocomplete="list"
        aria-controls={listboxId}
        aria-activedescendant={activeDescendantId}
      />
      {value && (
        <button
          type="button"
          className="lookup-field__search-clear"
          onClick={handleClear}
          aria-label="Clear search"
          tabIndex={-1}
        >
          <X className="h-4 w-4" />
        </button>
      )}
      {/*
        A la DERECHA del input y en la misma fila, pero EN FLUJO, no superpuestos.
        Superpuestos (`position: absolute`) es lo que forzaba la etiqueta de dos
        caracteres: con la palabra completa taparían lo que el usuario teclea. En flujo,
        el input cede el ancho que hace falta y nadie se solapa.
      */}
      {operadores}
    </div>
  )
})
